<script setup>
import { computed, ref } from "vue";
import { MUTED_POINT_COLORS, POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";
import { renderMarkdownInline } from "@utils/renderMarkdown";
import { removeHtmlMarkdownAndLineBreak, truncateText } from "@utils/toolBox";

const props = defineProps({
  /** Array of { label, color, items: [{ question_id, label, satisfaction, importance }] } */
  populations: { type: Array, required: true },
  /** { satisfaction: number, importance: number } */
  thresholds: { type: Object, required: true },
  thresholdMode: { type: String, default: "auto" },
});

// Chart dimensions
const width = 600;
const height = 400;
const margin = { top: 30, right: 30, bottom: 50, left: 60 };
const plotW = width - margin.left - margin.right;
const plotH = height - margin.top - margin.bottom;

// Compute all items across populations for scale calculation
const allItems = computed(() =>
  props.populations.flatMap((p) => p.items || []),
);

// X scale (satisfaction) — includes threshold so the dashed line is always visible
const xDomain = computed(() => {
  const vals = allItems.value.map((i) => i.satisfaction).filter((v) => v != null);
  vals.push(props.thresholds.satisfaction);
  if (!vals.length) return [0, 10];
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const pad = (max - min) * 0.15 || 1;
  return [Math.max(0, Math.floor(min - pad)), Math.min(10, Math.ceil(max + pad))];
});

// Y scale (importance / correlation) — includes threshold so the dashed line is always visible
const yDomain = computed(() => {
  const vals = allItems.value.map((i) => i.importance).filter((v) => v != null);
  vals.push(props.thresholds.importance);
  if (!vals.length) return [-0.2, 1];
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const pad = (max - min) * 0.15 || 0.1;
  return [Math.max(-1, +(min - pad).toFixed(2)), Math.min(1, +(max + pad).toFixed(2))];
});

function scaleX(val) {
  const [lo, hi] = xDomain.value;
  return ((val - lo) / (hi - lo)) * plotW;
}

function scaleY(val) {
  const [lo, hi] = yDomain.value;
  return plotH - ((val - lo) / (hi - lo)) * plotH;
}

// X axis ticks
const xTicks = computed(() => {
  const [lo, hi] = xDomain.value;
  const step = (hi - lo) <= 5 ? 0.5 : 1;
  const ticks = [];
  for (let v = lo; v <= hi; v += step) {
    ticks.push(+v.toFixed(1));
  }
  return ticks;
});

// Y axis ticks
const yTicks = computed(() => {
  const [lo, hi] = yDomain.value;
  const range = hi - lo;
  const step = range <= 0.5 ? 0.1 : range <= 1 ? 0.2 : 0.25;
  const ticks = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) {
    ticks.push(+v.toFixed(2));
  }
  return ticks;
});

// Threshold lines in plot coordinates
const thresholdX = computed(() => scaleX(props.thresholds.satisfaction));
const thresholdY = computed(() => scaleY(props.thresholds.importance));

// Quadrant labels
const quadrants = computed(() => [
  { label: "Act fast", x: margin.left + thresholdX.value / 2, y: margin.top + thresholdY.value / 2, class: "text-status-error" },
  { label: "Capitalize", x: margin.left + thresholdX.value + (plotW - thresholdX.value) / 2, y: margin.top + thresholdY.value / 2, class: "text-status-success-strong" },
  { label: "Monitor", x: margin.left + thresholdX.value / 2, y: margin.top + thresholdY.value + (plotH - thresholdY.value) / 2, class: "text-muted-foreground" },
  { label: "Maintain", x: margin.left + thresholdX.value + (plotW - thresholdX.value) / 2, y: margin.top + thresholdY.value + (plotH - thresholdY.value) / 2, class: "text-status-info" },
]);

function popColor(pop, idx) {
  return pop.color || POPULATION_AUTO_COLORS[idx] || POPULATION_AUTO_COLORS[0];
}

function itemFillColor(item, pop, popIdx) {
  if (item.variance_status === 'low') return MUTED_POINT_COLORS.fill;
  return popColor(pop, popIdx);
}

function itemStrokeColor(item, pop, popIdx, isActive) {
  if (item.variance_status === 'low') return MUTED_POINT_COLORS.stroke;
  return isActive ? 'currentColor' : 'white';
}

// Tooltip state
const svgRef = ref(null);
const tooltip = ref(null);

// The panel is about this tall; above that distance from the top it sits over the point, else under it.
const TOOLTIP_HEIGHT = 170;
const TOOLTIP_HALF_WIDTH = 110;

function showTooltip(event, item, pop, popIdx) {
  const svg = svgRef.value;
  if (!svg) return;
  const rect = svg.getBoundingClientRect();
  const circle = event.target;
  const cx = circle.getBoundingClientRect();
  const pointY = cx.top - rect.top;
  const below = pointY < TOOLTIP_HEIGHT;
  tooltip.value = {
    item,
    pop,
    popIdx,
    // Kept inside the chart's width: the scroll container clips what sticks out.
    x: Math.min(Math.max(cx.left + cx.width / 2 - rect.left, TOOLTIP_HALF_WIDTH), rect.width - TOOLTIP_HALF_WIDTH),
    y: below ? pointY + cx.height + 8 : pointY - 8,
    below,
  };
}

function hideTooltip() {
  tooltip.value = null;
}
</script>

<template>
  <div class="relative w-full overflow-x-auto print:overflow-visible">
    <svg ref="svgRef" :viewBox="`0 0 ${width} ${height}`" class="w-full max-w-[600px] mx-auto" preserveAspectRatio="xMidYMid meet">
      <!-- Plot area background -->
      <rect :x="margin.left" :y="margin.top" :width="plotW" :height="plotH" fill="transparent" />

      <!-- X axis grid + ticks -->
      <g>
        <template v-for="tick in xTicks" :key="`x-${tick}`">
          <line
            :x1="margin.left + scaleX(tick)" :y1="margin.top"
            :x2="margin.left + scaleX(tick)" :y2="margin.top + plotH"
            stroke="currentColor" class="text-border" stroke-width="0.5" stroke-dasharray="2,3" />
          <text
            :x="margin.left + scaleX(tick)" :y="margin.top + plotH + 16"
            text-anchor="middle" class="fill-muted-foreground" style="font-size: 10px;">
            {{ tick }}
          </text>
        </template>
        <text
          :x="margin.left + plotW / 2" :y="height - 6"
          text-anchor="middle" class="fill-muted-foreground" style="font-size: 11px; font-weight: 500;">
          Performance
        </text>
      </g>

      <!-- Y axis grid + ticks -->
      <g>
        <template v-for="tick in yTicks" :key="`y-${tick}`">
          <line
            :x1="margin.left" :y1="margin.top + scaleY(tick)"
            :x2="margin.left + plotW" :y2="margin.top + scaleY(tick)"
            stroke="currentColor" class="text-border" stroke-width="0.5" stroke-dasharray="2,3" />
          <text
            :x="margin.left - 8" :y="margin.top + scaleY(tick) + 3"
            text-anchor="end" class="fill-muted-foreground" style="font-size: 10px;">
            {{ tick }}
          </text>
        </template>
        <text
          :x="12" :y="margin.top + plotH / 2"
          text-anchor="middle" class="fill-muted-foreground" style="font-size: 11px; font-weight: 500;"
          :transform="`rotate(-90, 12, ${margin.top + plotH / 2})`">
          Importance
        </text>
      </g>

      <!-- Threshold lines -->
      <line
        :x1="margin.left + thresholdX" :y1="margin.top"
        :x2="margin.left + thresholdX" :y2="margin.top + plotH"
        stroke="currentColor" class="text-foreground/30" stroke-width="1.5" stroke-dasharray="6,4" />
      <line
        :x1="margin.left" :y1="margin.top + thresholdY"
        :x2="margin.left + plotW" :y2="margin.top + thresholdY"
        stroke="currentColor" class="text-foreground/30" stroke-width="1.5" stroke-dasharray="6,4" />

      <!-- Quadrant labels -->
      <text
        v-for="q in quadrants"
        :key="q.label"
        :x="q.x" :y="q.y"
        text-anchor="middle" dominant-baseline="middle"
        :class="q.class" style="font-size: 11px; font-weight: 600; opacity: 0.6;">
        {{ q.label }}
      </text>

      <!-- Axes borders -->
      <line :x1="margin.left" :y1="margin.top" :x2="margin.left" :y2="margin.top + plotH" stroke="currentColor" class="text-border" stroke-width="1" />
      <line :x1="margin.left" :y1="margin.top + plotH" :x2="margin.left + plotW" :y2="margin.top + plotH" stroke="currentColor" class="text-border" stroke-width="1" />

      <!-- Data points -->
      <template v-for="(pop, popIdx) in populations" :key="pop.label">
        <circle
          v-for="item in pop.items"
          :key="`${pop.label}-${item.question_id}`"
          :cx="margin.left + scaleX(item.satisfaction)"
          :cy="margin.top + scaleY(item.importance)"
          r="6"
          :fill="itemFillColor(item, pop, popIdx)"
          :stroke="itemStrokeColor(item, pop, popIdx, tooltip?.item.question_id === item.question_id && tooltip?.popIdx === popIdx)"
          :stroke-width="tooltip?.item.question_id === item.question_id && tooltip?.popIdx === popIdx ? 2 : 1.5"
          :stroke-dasharray="item.variance_status === 'low' ? '3,2' : null"
          class="cursor-pointer transition-all"
          @mouseenter="showTooltip($event, item, pop, popIdx)"
          @mouseleave="hideTooltip" />
      </template>

      <!-- Item labels (rendered once per item, using first population's positions) -->
      <text
        v-for="item in (populations[0]?.items || [])"
        :key="`label-${item.question_id}`"
        :x="margin.left + scaleX(item.satisfaction) + 10"
        :y="margin.top + scaleY(item.importance) + 4"
        class="fill-foreground" style="font-size: 10px; pointer-events: none;">
        {{ truncateText(removeHtmlMarkdownAndLineBreak(item.label)) }}
      </text>
    </svg>

    <!-- HTML tooltip -->
    <div
      v-if="tooltip"
      class="absolute z-50 pointer-events-none animate-in fade-in-0 zoom-in-95 border-border/50 bg-background text-foreground min-w-[12rem] rounded-lg border px-3 py-2 text-xs shadow-xl"
      :style="{ left: `${tooltip.x}px`, top: `${tooltip.y}px`, transform: tooltip.below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)' }">
      <div class="font-medium mb-1.5" v-html="renderMarkdownInline(tooltip.item.label)" />
      <div class="grid gap-1">
        <div class="flex justify-between gap-4">
          <span class="text-muted-foreground">Satisfaction</span>
          <span class="font-medium tabular-nums">{{ tooltip.item.satisfaction?.toFixed(2) }}</span>
        </div>
        <div class="flex justify-between gap-4">
          <span class="text-muted-foreground">Importance</span>
          <span class="font-medium tabular-nums">{{ tooltip.item.importance?.toFixed(2) }}</span>
        </div>
        <div class="flex justify-between gap-4">
          <span class="text-muted-foreground">Respondents</span>
          <span class="font-medium tabular-nums">{{ tooltip.item.respondents }}</span>
        </div>
        <div v-if="tooltip.item.stddev != null" class="flex justify-between gap-4">
          <span class="text-muted-foreground">Std. dev.</span>
          <span class="font-medium tabular-nums">{{ tooltip.item.stddev?.toFixed(2) }}</span>
        </div>
        <div v-if="tooltip.item.variance_status === 'low'" class="mt-1 pt-1 border-t border-border/50 text-status-warning-strong">
          Low variance (σ&nbsp;&lt;&nbsp;1.0) — correlation unreliable
        </div>
        <div v-else-if="tooltip.item.variance_status === 'limit'" class="mt-1 pt-1 border-t border-border/50 text-status-warning-strong">
          Borderline variance (σ&nbsp;&lt;&nbsp;1.5) — interpret with caution
        </div>
        <div v-if="populations.length > 1" class="flex items-center gap-1.5 mt-1 pt-1 border-t border-border/50">
          <span class="inline-block h-2.5 w-2.5 rounded-xs" :style="{ backgroundColor: popColor(tooltip.pop, tooltip.popIdx) }" />
          <span class="text-muted-foreground">{{ tooltip.pop.label }}</span>
        </div>
      </div>
    </div>

    <!-- Legend (multi-population) -->
    <div v-if="populations.length > 1" class="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
      <span
        v-for="(pop, idx) in populations"
        :key="pop.label"
        class="flex items-center gap-1.5">
        <span class="inline-block h-2.5 w-2.5 rounded-full" :style="{ backgroundColor: popColor(pop, idx) }" />
        {{ pop.label }}
      </span>
    </div>
  </div>
</template>
